import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAltFeature } from '../../context';
import { Region } from '../../utils';
import {
  AuthenticationProvider as AuthenticationProviderAuth0,
  useAuth as useAuthAuth0,
} from './auth0/auth';
import { UseAuthContextType } from './auth0/auth.types';
import { getDefaultUseAuthState } from './auth0/helpers';
import {
  AuthenticationProvider as AuthenticationProviderOverride,
  useAuth as useAuthOverride,
} from './override/auth';
import { store as overrideStore } from './override/store';
import {
  AuthenticationProvider as AuthenticationProviderPortal,
  useAuth as useAuthPortal,
} from './portal/auth';
import {
  AuthenticationProvider as AuthenticationProviderTest,
  useAuth as useAuthTest,
} from './test/auth';
import { isTestEnvironment } from './testUtils';

type AuthFactoryContextState = {
  useAuth: () => UseAuthContextType;
  overrideToken: string | undefined;
  setOverrideToken: (token: string | undefined) => void;
};

const DefaultUseAuthState = getDefaultUseAuthState();
const AuthFactoryDefaultState = {
  useAuth: () => DefaultUseAuthState,
  overrideToken: undefined,
  setOverrideToken: () => {},
};

export const AuthFactoryContext = createContext<AuthFactoryContextState>(
  AuthFactoryDefaultState,
);

export const AuthFactoryProvider = ({
  children,
  ...providerProps
}: {
  children: ReactElement;
  clientId: string;
  redirectUri?: string;
  onTokenReceived: (token: string, region: Region) => void;
}) => {
  const altAuthFeature = useAltFeature('core.auth');

  const [overrideToken, setOverrideTokenState] = useState<
    AuthFactoryContextState['overrideToken']
  >(overrideStore.get());

  const setOverrideToken = useCallback((token?: string) => {
    if (token) {
      overrideStore.set(token);
    } else {
      overrideStore.remove();
    }

    setOverrideTokenState(token);
  }, []);

  const { AuthenticationProvider: CurrentAuthenticationProvider, ...state } =
    useMemo(() => {
      const sharedState = {
        overrideToken,
        setOverrideToken,
      };

      // test setup is the highest priority
      if (isTestEnvironment()) {
        return {
          ...sharedState,
          AuthenticationProvider: AuthenticationProviderTest,
          useAuth: useAuthTest,
        };
      }

      // manual override is the next priority
      if (overrideToken) {
        return {
          ...sharedState,
          AuthenticationProvider: AuthenticationProviderOverride,
          useAuth: useAuthOverride,
        };
      }

      if (altAuthFeature === 'portal') {
        return {
          ...sharedState,
          AuthenticationProvider: AuthenticationProviderPortal,
          useAuth: useAuthPortal,
        };
      }

      // default to auth0

      return {
        ...sharedState,
        AuthenticationProvider: AuthenticationProviderAuth0,
        useAuth: useAuthAuth0,
      };
    }, [altAuthFeature, overrideToken, setOverrideToken]);

  return (
    <AuthFactoryContext.Provider value={state}>
      <CurrentAuthenticationProvider {...providerProps}>
        {children}
      </CurrentAuthenticationProvider>
    </AuthFactoryContext.Provider>
  );
};

export const useAuthFactory = () => useContext(AuthFactoryContext);

export const useAuth = () => {
  const { useAuth: currentUseAuth } = useAuthFactory();

  return currentUseAuth();
};
